import { useState, useEffect, useRef, useCallback, Suspense, createContext, lazy, useMemo } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { createClient } from "@supabase/supabase-js";
import Nav from "./components/Nav";
import Footer from "./components/Footer";
import EnquiryModal from "./components/EnquiryModal";
import SealLoader from "./components/SealLoader";

// ─── SUPABASE CLIENT (frontend — uses anon key for auth) ───
export const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL,
  process.env.REACT_APP_SUPABASE_ANON_KEY
);

export const ADMIN_UID = process.env.REACT_APP_ADMIN_UID;

// Helper: get current auth token for API calls
export async function getAuthToken() {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
}

// Helper: build auth headers for admin API calls
export async function authHeaders(extra = {}) {
  const token = await getAuthToken();
  return { ...extra, ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

// ─── TRANSLATIONS ───
export const T = {
  en: {
    navHome: "Home", navCatalog: "Catalogue", navDigitalTwins: "Documentation", navArchive: "The Archive", navAbout: "About",
    heroDesc: "Historic estates in Portugal and Spain, represented for private sale. Heritage objects and architectural elements documented to archival standard. Lisboa, Portugal.",
    ourWorkSub: "Three pillars of heritage practice",
    pillarEstatesTitle: "Historic Estates", pillarEstatesSub: "Brokerage",
    pillarEstatesDesc: "Manor houses, quintas, solares, and noble estates across Portugal and Spain.",
    pillarTwinsTitle: "Digital Twins", pillarTwinsSub: "Heritage Documentation",
    pillarTwinsDesc: "Museum-quality 3D scanning of historic objects, architectural elements, and sacred spaces.",
    pillarArchiveTitle: "The Archive", pillarArchiveSub: "A Living Record",
    pillarArchiveDesc: "A curated catalogue of significant heritage objects documented by Anachrona.",
    homeQuote: "Anachrona was founded in Lourinh\u00e3 with a conviction that Portugal\u2019s built heritage \u2014 its manor houses, its parish churches, its carved stone and gilded wood \u2014 deserves custodians who understand what they are inheriting.",
    ctaPrivate: "Private Consultation", ctaBegin: "Begin a Conversation",
    ctaDesc: "Whether you are seeking a historic estate or wish to document heritage through 3D scanning, we welcome your enquiry.",
    ctaBook: "Enquire",
    catalogTitle: "Historic Estates", catalogSub: "Historic estates in Portugal and Spain, available by private arrangement",
    country: "Country", type: "Type", searchProperties: "Search properties...",
    noProperties: "No properties currently match.",
    photographyForthcoming: "Photography forthcoming",
    priceOnRequest: "Price on Request",
    returnToCatalog: "Return to Catalogue", video: "Video", notableFeatures: "Notable Features",
    privateEnquiry: "Private Enquiry",
    yourName: "Your Name", emailOrTel: "Email or Telephone", yourMessage: "Your Message",
    sendEnquiry: "Send Enquiry", sending: "Sending...",
    enquirySent: "Thank you \u2014 your enquiry has been sent.",
    enquiryError: "Something went wrong. Please email us directly.",
    fillAllFields: "Please fill in all fields.",
    photographyPlaceholder: "Atmospheric photography",
    twinsTitle: "Digital Twins", twinsSub: "Museum-quality 3D documentation of heritage objects and spaces",
    twinsIntro: "A digital twin is a geometrically precise, photorealistic three-dimensional replica of a physical object or space. Created through structured-light scanning at sub-millimetre accuracy, it preserves not only the form but the surface \u2014 every chisel mark, every crack, every trace of polychrome.",
    twinsDemo: "Interactive demonstration \u2014 drag to rotate, scroll to zoom",
    techTitle: "The Technology", techText: "Anachrona employs state-of-the-art structured-light scanning technology, capable of capturing geometry at sub-millimetre resolution with full colour texture \u2014 producing results of the highest archival and academic standard.",
    whyTitle: "Why It Matters", whyText: "Heritage is fragile. Stone erodes, wood decays, gilding flakes, fire destroys. A high-resolution digital twin ensures that even if the physical object is lost or damaged, its form survives.",
    whatTitle: "What We Produce", whatText: "Each scan produces a dense polygon mesh with photographic texture, delivered as archival-quality files suitable for conservation records, academic publication, 3D printing, and interactive web presentation.",
    techLabel: "Technology", whyLabel: "Mission", whatLabel: "Output", twinsInfoTitle: "How We Document",
    dtCtaLabel: "Heritage Documentation", dtCtaTitle: "Book a Consultation",
    dtCtaDesc: "Discuss your scanning project directly \u2014 from individual objects and architectural elements to room-scale interiors.",
    dtCtaBtn: "Make an enquiry",
    requestConsultation: "Request Consultation",
    institutionOrRole: "Institution or Role", describeProject: "Describe your project",
    sendRequest: "Send Request",
    requestSent: "Thank you \u2014 your request has been sent.",
    requestError: "Something went wrong. Please email us directly.",
    fillRequired: "Please fill in all required fields.",
    archiveTitle: "The Archive", archiveSub: "A curated record of heritage objects documented by Anachrona",
    archiveIntro: "Each object has been documented through 3D scanning, photography, or both. Some may be available for acquisition through private consultation.",
    period: "Period", searchArchive: "Search the archive...",
    noObjects: "No objects match.",
    available: "Available",
    returnToArchive: "Return to Archive",
    hiResPhoto: "High-resolution photography",
    interactive3d: "Interactive 3D model \u2014 drag to rotate, scroll to zoom",
    date: "Date", origin: "Origin", collection: "Collection",
    availableNote: "This piece may be available for acquisition through private consultation.",
    footerLocation: "Lourinh\u00e3, Silver Coast, Portugal",
    loading: "Loading...",
  },
};

// ─── FONTS ───
export const FONT_LINK = "https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500&family=EB+Garamond:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&display=swap";

// ─── LOGOS ───
export const LOGO_SEAL_WHITE = "/anachrona-seal.svg";

export const HERO_PAINTING = "/hero-painting.jpg";
export const CATALOG_PAINTING = "/catalog-painting.jpg";
export const TWINS_PAINTING = "/twins-painting.jpg";

// ─── COLOR SYSTEM (Black & White) ───
export const ABOUT_PAINTING = "/about-painting.jpg";

export const C = {
  // Surfaces
  bg:          '#F8F6F0',
  bgCard:      '#EFEBDF',
  bgElevated:  '#EFEBDF',
  bgViewer:    '#EFEBDF',
  bgRaised:    '#EFEBDF',

  // Rules
  rule:        '#E4DECF',
  ruleSoft:    '#E4DECF',
  ruleStrong:  '#C9C1A8',

  // Type
  text:        '#0A0A0A',
  textSoft:    '#2B2A26',
  textMute:    '#6B6862',
  ink:         '#0A0A0A',
  inkSoft:     '#2B2A26',
  inkMute:     '#6B6862',
  vellum:      '#F8F6F0',

  // Gold — gold-deep for readable gold on vellum
  gold:        '#C4A35A',
  goldDeep:    '#9B7F3F',
  goldSoft:    '#D8C28C',
  accent:      '#9B7F3F',

  // Legacy aliases — mapped to nearest vellum equivalents
  accentMuted: '#D8C28C', // → goldSoft
  accentLight: '#D8C28C', // → goldSoft
  cream:       '#F8F6F0', // → vellum
  creamMuted:  '#6B6862', // → inkMute
  textMuted:   '#6B6862', // → inkMute
  border:      '#E4DECF', // → rule
  borderLight: '#E4DECF', // → ruleSoft
  fog:         '#6B6862', // → inkMute
};

export const CA = {
  bg: "#050505", card: "#0e0e0e", border: "#222222", input: "#0a0a0a",
  success: "#5a8a5a", successBg: "rgba(90,138,90,0.1)",
  danger: "#a85a5a", dangerBg: "rgba(168,90,90,0.1)",
  info: "rgba(255,255,255,0.05)",
};

// ─── ADMIN AUTH (Supabase Auth — no hardcoded password) ───

function mapProperty(p) {
  return {
    id: p.id,
    name: p.name,
    location: p.location,
    country: p.country,
    coordinates: p.coordinates || null,
    type: p.type,
    period: p.period,
    price: p.price,
    shortDesc: p.short_desc,
    fullDesc: p.full_desc,
    provenance: p.provenance || null,
    inscription: p.inscription || null,
    modelId: p.model_id,
    features: p.features || [],
    images: [],
    videos: [],
    primaryImage: p.primary_image || null,
    coverImage: p.cover_image || null,
    published: p.published !== false,
    protected: p.protected === true,
    hasPassword: p.has_password === true,
    propertyPassword: "",
    modelCaptionTitle: p.model_caption_title || null,
    modelCaptionBody: p.model_caption_body || null,
  };
}

export const ARCHIVE_PERIODS = ["Medieval", "Manueline", "Renaissance", "Baroque", "Neoclassical", "19th Century"];
export const ARCHIVE_TYPES = ["Sacred Art", "Architectural Elements", "Furniture", "Heraldry", "Azulejos"];

// ─── PERSISTENT STATE HOOK ───
export function usePersistedState(key, defaultValue) {
  const [state, setState] = useState(defaultValue);
  const [loaded, setLoaded] = useState(false);
  useEffect(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) setState(JSON.parse(raw));
    } catch(e) {}
    setLoaded(true);
  }, [key]);
  const setPersisted = useCallback((newVal) => {
    setState(prev => {
      const value = typeof newVal === "function" ? newVal(prev) : newVal;
      try { localStorage.setItem(key, JSON.stringify(value)); } catch(e) { console.error("Storage write failed:", e); }
      return value;
    });
  }, [key]);
  return [state, setPersisted, loaded];
}

// ─── 3D VIEWER LOADING FALLBACK ───
// Plain DOM, no three/fiber/drei dependency — used as the outer
// React.lazy Suspense fallback for the code-split ModelViewer/
// AdminModelEditor modules (src/components/ModelViewer/), matching the
// same thin-progress-bar treatment those modules show internally while
// the model itself is loading.
export function ModelViewerLoadingFallback() {
  return (
    <div style={{ width: "100%", height: "100%", background: C.bgViewer, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <SealLoader size="min(200px, 28vw)" color={C.goldDeep} />
    </div>
  );
}

// ─── DECORATIVE ───
export function Ornament({ style: s }) {
  return (<div style={{ display: "flex", alignItems: "center", gap: 0, ...s }}><div style={{ flex: 1, height: 1, background: `linear-gradient(to right, transparent, ${C.border})` }} /><span style={{ fontSize: 15, color: C.goldDeep, padding: "0 12px" }}>✠</span><div style={{ flex: 1, height: 1, background: `linear-gradient(to left, transparent, ${C.border})` }} /></div>);
}
export function SectionTitle({ children, subtitle, subtitleColor }) {
  return (<div style={{ textAlign: "center", marginBottom: 48 }}><h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(36px, 5.5vw, 64px)", fontWeight: 300, color: C.cream, letterSpacing: 4, textTransform: "uppercase", margin: 0, lineHeight: 1.15 }}>{children}</h2>{subtitle && <p style={{ fontFamily: "'EB Garamond', serif", fontSize: "clamp(15px, 1.8vw, 19px)", fontStyle: "italic", color: subtitleColor || "#e8e8e8", marginTop: 16, letterSpacing: 0.5 }}>{subtitle}</p>}</div>);
}

const ADMIN_BTN_VARIANTS = { primary: { border: `1px solid ${C.accentMuted}`, color: C.accent }, danger: { border: `1px solid ${CA.danger}`, color: CA.danger }, success: { border: `1px solid ${CA.success}`, color: CA.success }, ghost: { border: `1px solid ${CA.border}`, color: C.textMuted } };
export function AdminBtn({ children, onClick, variant = "primary", style: s, disabled }) {
  const vs = ADMIN_BTN_VARIANTS;
  return (<button onClick={onClick} disabled={disabled} style={{ background: "transparent", ...vs[variant], padding: "10px 24px", fontFamily: "'EB Garamond', serif", fontSize: 13, letterSpacing: 2, textTransform: "uppercase", cursor: disabled ? "not-allowed" : "pointer", borderRadius: 4, transition: "all 0.3s ease", opacity: disabled ? 0.4 : 1, ...s }} onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = CA.info; }} onMouseLeave={e => { e.currentTarget.style.background = "transparent"; }}>{children}</button>);
}
export function AdminToast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (<div style={{ position: "fixed", top: 20, right: 20, zIndex: 10000, background: type === "success" ? "#1a2a1a" : type === "error" ? "#2a1414" : CA.card, border: `1px solid ${type === "success" ? CA.success : type === "error" ? CA.danger : CA.border}`, padding: "14px 24px", borderRadius: 6, fontFamily: "'EB Garamond', serif", fontSize: 14, color: type === "success" ? "#8aba8a" : type === "error" ? "#ca8a8a" : C.cream, animation: "slideIn 0.3s ease", boxShadow: "0 8px 24px rgba(0,0,0,0.4)" }}>{message}</div>);
}

// ═══════════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════════
export const AppContext = createContext({});

const HomePage = lazy(() => import("./pages/HomePage"));
const CatalogPage = lazy(() => import("./pages/CatalogPage"));
const PropertyDetail = lazy(() => import("./pages/PropertyDetail"));
const DigitalTwinsPage = lazy(() => import("./pages/DigitalTwinsPage"));
const ArchivePage = lazy(() => import("./pages/ArchivePage"));
const ArchiveDetail = lazy(() => import("./pages/ArchiveDetail"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const AdminPanel = lazy(() => import("./components/AdminPanel"));
const AdminLoginGate = lazy(() => import("./components/AdminPanel").then(m => ({ default: m.AdminLoginGate })));

// Blank on purpose: during the first load this sits behind the boot veil, so
// anything drawn here would be invisible anyway. On later route changes the
// nav (with its own seal) stays put and only the content area swaps, so a
// second large seal here read as a redundant double-glyph.
function PageLoader() {
  return (
    <div style={{ minHeight: "100vh", background: C.bg }} />
  );
}

// Fires once the lazy route chunk has actually committed. Because it sits
// inside the same <Suspense> boundary as <Routes>, React withholds it from
// the commit until the route resolves — so its mount is a reliable "the real
// page is now on screen" signal, which is what the boot veil waits for
// before fading out. The double rAF guarantees the veil has painted at full
// opacity for at least one frame first, so the fade actually transitions
// instead of being collapsed into the same commit.
// Eases page content in on navigation, so switching tabs dissolves rather
// than snapping. Keyed by pathname: without a changing key React reuses the
// same DOM node across routes and a CSS animation on it never restarts, so
// the fade would only ever play once. Subscribing to the location here
// rather than in the app root keeps route changes from re-rendering
// everything else (nav, modals, the veil).
function RouteFade({ children }) {
  const location = useLocation();
  return <div key={location.pathname} className="route-fade">{children}</div>;
}

function RouteReadySignal({ onReady }) {
  useEffect(() => {
    let inner;
    const outer = requestAnimationFrame(() => { inner = requestAnimationFrame(onReady); });
    return () => { cancelAnimationFrame(outer); if (inner) cancelAnimationFrame(inner); };
  }, [onReady]);
  return null;
}

// Fixed clearance so an anchored section's heading isn't hidden beneath the
// fixed nav (92px tall, unscrolled) when scrolled into view via hash.
const HASH_SCROLL_OFFSET = 100;

// Scrolls a same-page element into view, clearing the fixed header. Shared by
// ScrollToTop (cross-page hash navigation, e.g. /about#founder) and any
// same-page anchor button (e.g. the homepage's "For Owners" hero CTA) so
// there's a single offset-scroll implementation, not two.
export function scrollToHashTarget(id, { poll = false } = {}) {
  let attempts = 0;
  let raf;
  const tryScroll = () => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.pageYOffset - HASH_SCROLL_OFFSET;
      window.scrollTo({ top, behavior: "smooth" });
    } else if (poll && attempts < 30) {
      attempts += 1;
      raf = requestAnimationFrame(tryScroll);
    }
  };
  raf = requestAnimationFrame(tryScroll);
  return () => cancelAnimationFrame(raf);
}

function ScrollToTop() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) {
      window.scrollTo(0, 0);
      return;
    }
    // React Router doesn't scroll to hash fragments on navigation, and the
    // target page may still be loading (routes are lazy + Suspense), so the
    // element isn't guaranteed to exist yet on the first frame — poll a few
    // frames for it rather than giving up immediately.
    return scrollToHashTarget(location.hash.slice(1), { poll: true });
  }, [location.pathname, location.hash]);
  return null;
}

// Fixed full-page painted/ink plane shared by the home, catalogue, documentation
// and about routes. Sits at z-index:-1 behind all content (incl. the shared Footer
// and any opaque parchment cards, which cover it). Dark base → painting → overlay
// gradient tuned for legible light text while keeping the painting "atmospheric"
// not focal. Each route shows its OWN painting; framing (background-position),
// painting opacity, and the overlay gradient are tuned per image, since each source
// differs in brightness and composition. Defaults (DEFAULT_OPACITY / DEFAULT_OVERLAY)
// are the homepage-tuned values; a route overrides them only where its image needs it.
// background-attachment:fixed gives the parallax on desktop; the layer is also
// position:fixed. Attachment is chosen in JS (see PageBackdrop): fixed on desktop
// where it's supported, scroll on mobile and on browsers that lack fixed support.
// This matters because iOS Safari *reports* support for background-attachment:fixed
// (so a plain `@supports not (...)` CSS fallback never triggers there) yet renders it
// by sizing the image against the full document height, not the viewport — which is
// what made the backdrop read as extremely zoomed-in/dark on a narrow portrait phone.
//
// Mobile tuning: a portrait viewport crops these (mostly landscape) paintings far
// tighter than the desktop landscape crop they were tuned against, so a phone lands on
// darker texture/shadow. Below <=767px we switch to scroll attachment, raise painting
// opacity, and lighten the overlay so the plane reads as bright as it does on desktop.
// Each route may override the mobile values (mobileOpacity / mobileOverlay /
// mobilePosition / mobileSize); it inherits the DEFAULT_MOBILE_* values otherwise.
const DEFAULT_OPACITY = 0.34;
const DEFAULT_OVERLAY = "linear-gradient(to bottom, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.75) 100%)";
const DEFAULT_MOBILE_OPACITY = 0.44;
const DEFAULT_MOBILE_OVERLAY = "linear-gradient(to bottom, rgba(0,0,0,0.12) 0%, rgba(0,0,0,0.28) 42%, rgba(0,0,0,0.64) 100%)";

const BACKDROP_BY_ROUTE = {
  "/":              { image: HERO_PAINTING,    position: "center 15%" },
  "/catalogue":     { image: CATALOG_PAINTING, position: "center 90%",
                      // Desktop frames the low "center 90%" band; on a tight portrait
                      // crop that band is mostly foreground shadow, so pull the mobile
                      // crop back toward the middle of the composition.
                      mobilePosition: "center 65%" },
  // Vermeer "The Geographer" — a dark, tall interior. At "center 20%" the crop sat on
  // the dim window/cabinet and clipped the subject's face; dropped to "center 50%" to
  // frame the lit figure and the foreground carpet/desk. Opacity nudged up 0.34→0.37
  // and the overlay lightened ~12% from the default (same proportion as the homepage
  // brighten) so the darker source reads as light as the hero does.
  "/digital-twins": { image: TWINS_PAINTING,   position: "center 50%", opacity: 0.37,
                      overlay: "linear-gradient(to bottom, rgba(0,0,0,0.13) 0%, rgba(0,0,0,0.30) 40%, rgba(0,0,0,0.66) 100%)",
                      // Darkest source → push mobile opacity a little past the default
                      // brighten and keep the already-lightened overlay.
                      mobileOpacity: 0.47,
                      mobileOverlay: "linear-gradient(to bottom, rgba(0,0,0,0.10) 0%, rgba(0,0,0,0.24) 40%, rgba(0,0,0,0.60) 100%)" },
  // Baroque "Angel of Fame" — a bright, pale-sky source. It needs a HEAVIER overlay
  // than the default (not lighter) to seat the pale painting into a dark enough plane
  // for the on-plane light text to stay legible.
  "/about":         { image: ABOUT_PAINTING,   position: "center 25%", opacity: 0.33,
                      overlay: "linear-gradient(to bottom, rgba(0,0,0,0.26) 0%, rgba(0,0,0,0.44) 45%, rgba(0,0,0,0.78) 100%)",
                      // Pale source: don't apply the default mobile brighten wholesale
                      // or light text loses contrast. Lift opacity only slightly and keep
                      // a substantial (just gently lightened) overlay for legibility.
                      mobileOpacity: 0.37,
                      mobileOverlay: "linear-gradient(to bottom, rgba(0,0,0,0.22) 0%, rgba(0,0,0,0.40) 45%, rgba(0,0,0,0.72) 100%)" },
};

// True for the routes that render the fixed full-page painting plane (home,
// catalogue, documentation, about). Shared with Nav so the navbar can sit
// transparent over the painting at the top of these pages only.
export const hasPageBackdrop = (pathname) =>
  Object.prototype.hasOwnProperty.call(BACKDROP_BY_ROUTE, pathname);

// Routes that render entirely from static content — no catalogue, archive or
// model data is read to paint them. The boot veil holds for the Supabase
// round trip only on the routes that genuinely need it; landing on one of
// these used to mean staring at the veil while the catalogue and every
// property's gallery loaded for a page that never shows them.
// Keep this in step with the routes below: a page that starts reading
// `archive`, `properties` or `models` from context must come OUT of this set,
// or the veil will lift on it while it is still empty.
const DATA_FREE_ROUTES = new Set(["/", "/about", "/privacy"]);
export const pathNeedsBootData = (pathname) => {
  // The router treats "/about/" as "/about"; match that so a trailing slash
  // doesn't silently fall through to the slower, data-waiting branch.
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") || "/" : pathname;
  return !DATA_FREE_ROUTES.has(normalized);
};

// Whether the browser actually honours background-attachment:fixed. iOS Safari
// reports true here but renders it incorrectly, so we treat the mobile breakpoint as
// the real signal and only use fixed on non-mobile viewports (see PageBackdrop).
const SUPPORTS_FIXED_BG =
  typeof window !== "undefined" && window.CSS && typeof window.CSS.supports === "function"
    ? window.CSS.supports("background-attachment", "fixed")
    : true;

function PageBackdrop() {
  const location = useLocation();
  // <=767px matches the mobile breakpoint used across the backdrop pages (isMedium)
  // and the max-width:768px CSS breakpoint elsewhere in the app.
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 767);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);

  const cfg = BACKDROP_BY_ROUTE[location.pathname];

  // Dev-only diagnostic: confirms which attachment branch a given device actually takes
  // (notably that iOS Safari reports fixed support yet must be forced onto the mobile
  // scroll path). No-op in production builds.
  useEffect(() => {
    if (process.env.NODE_ENV === "production" || !cfg) return;
    const attachment = isMobile || !SUPPORTS_FIXED_BG ? "scroll" : "fixed";
    // eslint-disable-next-line no-console
    console.debug(
      `[PageBackdrop] ${location.pathname} | viewport ${window.innerWidth}x${window.innerHeight}` +
      ` | CSS.supports(fixed)=${SUPPORTS_FIXED_BG} | mobile=${isMobile} | attachment=${attachment}`
    );
  }, [location.pathname, isMobile, cfg]);

  if (!cfg) return null;

  const attachment = isMobile || !SUPPORTS_FIXED_BG ? "scroll" : "fixed";
  const opacity = isMobile ? (cfg.mobileOpacity ?? DEFAULT_MOBILE_OPACITY) : (cfg.opacity ?? DEFAULT_OPACITY);
  const overlay = isMobile ? (cfg.mobileOverlay ?? DEFAULT_MOBILE_OVERLAY) : (cfg.overlay ?? DEFAULT_OVERLAY);
  const position = isMobile ? (cfg.mobilePosition ?? cfg.position) : cfg.position;
  const size = isMobile ? (cfg.mobileSize ?? "cover") : "cover";

  // Mobile-only: promote the fixed backdrop to its own compositor layer so the
  // browser can't apply the transient scale it otherwise puts on a position:fixed
  // full-screen layer while the address bar collapses/expands during scroll —
  // that scale was reading as the painting "zooming in" and snapping choppily on
  // fast scroll. The art stays pinned and static instead. Desktop is left alone:
  // a transform here would neutralise the child's background-attachment:fixed and
  // kill the desktop parallax, and desktop doesn't have the collapsing toolbar.
  //
  // NOTE: this does not fully eliminate the scroll-snap on Brave for iOS — a known,
  // accepted limitation (see .wolf/buglog.json bug-008). Safari, the primary target,
  // renders correctly.
  const stabilize = isMobile
    ? { transform: "translateZ(0)", backfaceVisibility: "hidden", WebkitBackfaceVisibility: "hidden" }
    : null;

  return (
    <div aria-hidden="true" style={{
      position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh",
      zIndex: -1, background: C.ink, pointerEvents: "none",
      ...stabilize,
    }}>
      <div className="home-backdrop-art" style={{
        position: "absolute", inset: 0,
        backgroundImage: `url(${cfg.image})`,
        backgroundSize: size,
        backgroundPosition: position,
        backgroundAttachment: attachment,
        opacity,
      }} />
      <div style={{
        position: "absolute", inset: 0,
        background: overlay,
      }} />
    </div>
  );
}

export default function AnachronaApp() {
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminPanelOpen, setAdminPanelOpen] = useState(false);
  const [showPwGate, setShowPwGate] = useState(false);
  const archiveScrollRef = useRef(0);
  const [lang, setLang] = useState("en");
  const t = T[lang];
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [eqSubject, setEqSubject] = useState("General");
  const [eqForm, setEqForm] = useState({ name: "", contact: "", role: "", message: "", telephone: "" });
  const [eqStatus, setEqStatus] = useState(null);
  const openEnquiry = useCallback((type = "General") => {
    setEqSubject(type);
    setEqForm({ name: "", contact: "", role: "", message: "", telephone: "" });
    setEqStatus(null);
    setEnquiryOpen(true);
  }, []);
  const sendEnquiry = () => {
    if (!eqForm.name || !eqForm.contact || !eqForm.message) { setEqStatus(t.fillAllFields || "Please fill all required fields."); return; }
    setEqStatus("sending");
    const body = { name: eqForm.name, email: eqForm.contact, message: eqForm.message, enquiry_type: eqSubject };
    if (eqForm.role) body.role = eqForm.role;
    if (eqForm.telephone) body.telephone = eqForm.telephone;
    fetch(`/api/contact`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) })
      .then(r => { if (!r.ok) throw new Error(); setEqStatus("sent"); })
      .catch(() => setEqStatus("error"));
  };

  const [archive, setArchive] = useState([]);
  const [properties, setProperties] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [models, setModels, mL] = usePersistedState("anachrona-models", []);
  const [dtModelId, setDtModelId] = usePersistedState("anachrona-dt-model-id", null);
  const [modelBlobs, setModelBlobs] = useState({});
  const [propertyMedia, setPropertyMedia] = useState({});

  // ─── Boot veil ───
  // The seal-on-vellum screen shown until the site is genuinely ready. It is
  // an overlay rather than an either/or branch on purpose: the finished page
  // renders underneath it and is complete before the veil starts to go, so
  // the veil can fade out to reveal it. Swapping between two mutually
  // exclusive returns (what this used to do) unmounts one and mounts the
  // other in a single frame, which no amount of styling can make continuous.
  //
  // Evaluated once, from the entry URL: the veil only ever covers the FIRST
  // route, so the pathname at mount is the right question to ask. Reading it
  // from the router instead would subscribe the app root to every navigation
  // and re-render the whole tree (nav, modals, backdrop) on each one.
  const [bootNeedsData] = useState(() =>
    typeof window === "undefined" ? true : pathNeedsBootData(window.location.pathname)
  );
  const dataReady = bootNeedsData ? (dataLoaded && mL) : mL;
  const [routeReady, setRouteReady] = useState(false);
  const [veilGone, setVeilGone] = useState(false);
  const markRouteReady = useCallback(() => setRouteReady(true), []);
  const veilFading = dataReady && routeReady;

  // Drop the veil from the tree once its fade has finished, so it can't sit
  // invisibly over the page swallowing clicks.
  useEffect(() => {
    if (!veilFading) return;
    const id = setTimeout(() => setVeilGone(true), 900);
    return () => clearTimeout(id);
  }, [veilFading]);

  const dtModel = useMemo(() => {
    if (!models.length) return null;
    if (dtModelId) return models.find(m => m.id === dtModelId) || null;
    return models.find(m => m.dt_featured) || models[0] || null;
  }, [models, dtModelId]);

  // Restore Supabase auth session on load + listen for changes
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session && session.user.id === ADMIN_UID) setIsAdmin(true);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsAdmin(!!(session && session.user.id === ADMIN_UID));
    });
    return () => subscription.unsubscribe();
  }, []);

  // Load all data on mount in parallel
  useEffect(() => {
    const loadData = async () => {
      try {
        // Every read the first paint depends on goes out in ONE parallel wave.
        // property_media used to be fetched afterwards, one request per
        // property — a second round trip that couldn't start until this one
        // finished, then N requests serialised six-at-a-time by the browser's
        // per-host connection cap. Pulling the whole table here instead costs
        // a single extra concurrent request and no extra latency at all; the
        // rows are grouped by property below. RLS governs what comes back,
        // exactly as it did for the per-property queries.
        const [modelsData, propsData, archiveData, mediaData] = await Promise.all([
          supabase.from("models").select("*").order("created_at", { ascending: false }).then(({ data, error }) => error ? [] : data),
          fetch(`/api/properties`).then(r => r.json()),
          supabase.from("archive").select("*").order("created_at", { ascending: false }).then(({ data, error }) => error ? [] : data),
          supabase.from("property_media").select("*").then(({ data, error }) => error ? [] : data),
        ]);

        // Process models
        let mappedModels = [];
        let modelByArchiveId = {};
        if (Array.isArray(modelsData) && modelsData.length > 0) {
          mappedModels = modelsData.map(m => ({ ...m, modelUrl: m.public_url, fileName: m.filename }));
          setModels(mappedModels);
          mappedModels.forEach(m => { if (m.archive_item_id) modelByArchiveId[m.archive_item_id] = m; });
          const featured = mappedModels.find(m => m.dt_featured);
          if (featured) setDtModelId(featured.id);
        }
        const mappedProps = propsData.map(mapProperty);
        const mappedArchive = archiveData.map(a => ({
          id: a.id,
          ref: a.ref || null,
          nameEn: a.name_en,
          namePt: a.name_pt,
          period: a.period,
          date: a.date,
          origin: a.origin,
          type: a.type,
          surface: a.surface || null,
          collection: a.collection,
          available: a.available,
          has3d: a.has_3d,
          modelId: a.model_id,
          modelUrl: null,
          modelDetailUrl: null,
          description: a.description,
          primaryImage: a.primary_image || null,
          coverImage: a.cover_image || null,
        }));
        // Apply model->archive assignments inline before setting state
        const mappedArchiveWithModels = Object.keys(modelByArchiveId).length > 0
          ? mappedArchive.map(a => {
              const m = modelByArchiveId[a.id];
              return m ? { ...a, modelId: m.id, modelUrl: m.public_url, has3d: true } : a;
            })
          : mappedArchive;
        setProperties(mappedProps);
        setArchive(mappedArchiveWithModels);
        // Group the single media read above by property. Row order within a
        // property is preserved as the server returned it — the same
        // (unordered) guarantee the per-property queries gave.
        const mediaMap = {};
        propsData.forEach(p => { mediaMap[p.id] = { images: [], videos: [] }; });
        (mediaData || []).forEach(m => {
          const bucket = mediaMap[m.property_id];
          if (!bucket) return;
          if (m.type === "image") bucket.images.push(m);
          else if (m.type === "embed") bucket.videos.push(m);
        });
        setPropertyMedia(mediaMap);
      } catch (err) {
        console.error("Failed to load data:", err);
      } finally {
        setDataLoaded(true);
      }
    };
    loadData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // When admin logs in, reload properties with auth so unpublished properties appear
  useEffect(() => {
    if (!isAdmin) return;
    const reloadAsAdmin = async () => {
      try {
        const headers = await authHeaders();
        const propsData = await fetch(`/api/properties`, { headers }).then(r => r.json());
        if (!Array.isArray(propsData)) return;
        const mappedProps = propsData.map(mapProperty);
        setProperties(mappedProps);
      } catch (err) {
        console.error("Admin property reload failed:", err);
      }
    };
    reloadAsAdmin();
  }, [isAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keyboard shortcut: Ctrl+Shift+A to open admin
  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "A" || e.key === "a")) {
        e.preventDefault();
        if (!isAdmin) setShowPwGate(true);
        else setAdminPanelOpen(true);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isAdmin]);

  const saveProperty = async (property) => {
    try {
      const headers = await authHeaders({ "Content-Type": "application/json" });
      const res = await fetch(`/api/properties/${property.id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({
          name: property.name,
          location: property.location,
          country: property.country,
          coordinates: property.coordinates || null,
          type: property.type,
          period: property.period,
          price: property.price,
          short_desc: property.shortDesc,
          full_desc: property.fullDesc,
          provenance: property.provenance || null,
          inscription: property.inscription || null,
          model_id: property.modelId,
          features: property.features || [],
          primary_image: property.primaryImage || null,
          cover_image: property.coverImage || null,
          published: property.published !== false,
          protected: property.protected === true,
          property_password: property.propertyPassword || null,
          model_caption_title: property.modelCaptionTitle || null,
          model_caption_body: property.modelCaptionBody || null,
        })
      });
      if (!res.ok) throw new Error("Save failed");
      const saved = await res.json();
      const mapped = {
        id: saved.id,
        name: saved.name,
        location: saved.location,
        country: saved.country,
        coordinates: saved.coordinates || null,
        type: saved.type,
        period: saved.period,
        price: saved.price,
        shortDesc: saved.short_desc,
        fullDesc: saved.full_desc,
        provenance: saved.provenance || null,
        inscription: saved.inscription || null,
        modelId: saved.model_id,
        features: saved.features || [],
        images: property.images || [],
        videos: property.videos || [],
        primaryImage: saved.primary_image || null,
        coverImage: saved.cover_image || null,
        published: saved.published !== false,
        protected: saved.protected === true,
        hasPassword: saved.has_password === true,
        propertyPassword: "",
        modelCaptionTitle: saved.model_caption_title || null,
        modelCaptionBody: saved.model_caption_body || null,
      };
      setProperties(prev => {
        const exists = prev.find(p => p.id === saved.id);
        return exists ? prev.map(p => p.id === saved.id ? mapped : p) : [...prev, mapped];
      });
    } catch (err) {
      console.error("Failed to save property:", err);
      alert("Failed to save property");
    }
  };

  const deleteProperty = async (id) => {
    try {
      const { error } = await supabase.from("properties").delete().eq("id", id);
      if (error) throw error;
      setProperties(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error("Failed to delete property:", err);
      alert("Failed to delete property");
    }
  };

  const saveArchiveEntry = async (entry) => {
    try {
      // primary_image/cover_image/seo_* are intentionally omitted — the `archive` table has
      // no such columns; the old server route silently ignored them too (not destructured).
      const { data: saved, error } = await supabase
        .from("archive")
        .upsert({
          id: entry.id,
          ref: entry.ref || null,
          name_en: entry.nameEn,
          name_pt: entry.namePt,
          period: entry.period,
          date: entry.date,
          origin: entry.origin,
          type: entry.type,
          surface: entry.surface || null,
          collection: entry.collection,
          available: entry.available,
          has_3d: entry.has3d,
          model_id: entry.modelId,
          description: entry.description,
          updated_at: new Date().toISOString(),
        }, { onConflict: "id" })
        .select()
        .single();
      if (error) throw error;
      const model = models.find(m => m.id === saved.model_id);
      const mapped = {
        id: saved.id,
        ref: saved.ref || null,
        nameEn: saved.name_en,
        namePt: saved.name_pt,
        period: saved.period,
        date: saved.date,
        origin: saved.origin,
        type: saved.type,
        surface: saved.surface || null,
        collection: saved.collection,
        available: saved.available,
        has3d: saved.has_3d,
        modelId: saved.model_id,
        modelUrl: model?.modelUrl || model?.public_url || null,
        modelDetailUrl: null,
        description: saved.description,
        primaryImage: saved.primary_image || null,
        coverImage: saved.cover_image || null,
      };
      setArchive(prev => {
        const exists = prev.find(a => a.id === saved.id);
        return exists ? prev.map(a => a.id === saved.id ? mapped : a) : [...prev, mapped];
      });
    } catch (err) {
      console.error("Failed to save archive entry:", err);
      alert("Failed to save archive entry");
    }
  };

  const deleteArchiveEntry = async (id) => {
    try {
      const { error } = await supabase.from("archive").delete().eq("id", id);
      if (error) throw error;
      setArchive(prev => prev.filter(a => a.id !== id));
    } catch (err) {
      console.error("Failed to delete archive entry:", err);
      alert("Failed to delete archive entry");
    }
  };

  const contextValue = useMemo(() => ({
    lang, setLang, t,
    archive, setArchive,
    properties, setProperties,
    propertyMedia, setPropertyMedia,
    models, setModels,
    dtModel, dtModelId, setDtModelId,
    modelBlobs, setModelBlobs,
    archiveScrollRef,
    openEnquiry,
    // Lets the detail routes tell "this id doesn't exist" apart from "the
    // fetch hasn't landed yet". They now mount while it is still in flight,
    // so without this a direct link would bounce straight back to the index.
    dataLoaded,
  }), [lang, t, archive, properties, propertyMedia, models, dtModel, dtModelId, modelBlobs, openEnquiry, dataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppContext.Provider value={contextValue}>
      <link rel="icon" type="image/svg+xml" href={LOGO_SEAL_WHITE} />
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { background: ${C.bg}; color: ${C.text}; overflow-x: hidden; }
        ::selection { background: ${C.goldDeep}; color: ${C.vellum}; }
        input:focus, textarea:focus, select:focus { border-color: ${C.accentMuted} !important; outline: none; }
        @keyframes pulse { 0%, 100% { opacity: 0.4; } 50% { opacity: 1; } }
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(10px) translateX(-50%); } to { opacity: 0.6; transform: translateY(0) translateX(-50%); } }
        @keyframes slideIn { from { opacity: 0; transform: translateX(20px); } to { opacity: 1; transform: translateX(0); } }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        .boot-veil {
          position: fixed; inset: 0; z-index: 9999;
          background: ${C.bg};
          display: flex; align-items: center; justify-content: center;
          opacity: 1;
          transition: opacity 0.8s ease-in-out;
        }
        .boot-veil-out { opacity: 0; pointer-events: none; }
        /* The seal clears well before the vellum behind it does. Fading both
           together left the seal compositing over whatever the veil was
           uncovering — over a dark page (the model viewer especially) a
           half-faded gold seal reads as a big black smudge. Retiring it first
           means it is always gold or gone, never a dark ghost. */
        .boot-veil-seal { opacity: 1; transition: opacity 0.3s ease-in; }
        .boot-veil-out .boot-veil-seal { opacity: 0; }
        @keyframes routeFade { from { opacity: 0; } to { opacity: 1; } }
        .route-fade { animation: routeFade 0.35s ease-out; }
        @media (prefers-reduced-motion: reduce) {
          .boot-veil { transition: none; }
          .route-fade { animation: none; }
        }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-track { background: ${C.bg}; }
        ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${C.accentMuted}; }
        @media (max-width: 768px) { .desktop-nav { display: none !important; } .mobile-burger { display: flex !important; } .hide-mobile { display: none !important; } .hide-desktop { display: block !important; } .hide-on-mobile { display: none !important; } }
        @media (min-width: 769px) { .hide-desktop { display: none !important; } }
        @media (min-width: 769px) { .mobile-burger { display: none !important; } }
      `}</style>

      {showPwGate && (
        <Suspense fallback={<PageLoader />}>
          <AdminLoginGate onSuccess={() => { setShowPwGate(false); setAdminPanelOpen(true); }} onCancel={() => setShowPwGate(false)} />
        </Suspense>
      )}

      {/* position:relative makes this wrapper the containing block for the mobile
          absolute PageBackdrop, so its inset:0 fills the FULL document height, not just
          the viewport. Harmless on desktop: position:relative does NOT create a
          containing block for the desktop position:fixed backdrop, so its
          viewport-relative parallax is untouched. */}
      <div style={{ position: "relative", minHeight: "100vh", fontFamily: "'EB Garamond', serif" }}>
        {/* Rendered from the first frame, not gated on the fetch. The veil is
            already covering this, and holding the tree back meant the lazy
            route chunk didn't even begin downloading until every query had
            come back — two network phases in series where they can overlap
            completely. The detail routes read `dataLoaded` from context so an
            empty catalogue mid-flight doesn't read as "not found". */}
        {isAdmin && adminPanelOpen ? (
          <Suspense fallback={<PageLoader />}>
            <AdminPanel archive={archive} setArchive={setArchive} properties={properties} setProperties={setProperties} models={models} setModels={setModels} modelBlobs={modelBlobs} setModelBlobs={setModelBlobs} propertyMedia={propertyMedia} setPropertyMedia={setPropertyMedia} dtModelId={dtModelId} setDtModelId={setDtModelId} onExit={() => setAdminPanelOpen(false)} onSignOut={async () => { await supabase.auth.signOut(); setIsAdmin(false); setAdminPanelOpen(false); }} onSaveArchive={saveArchiveEntry} onDeleteArchive={deleteArchiveEntry} onSaveProperty={saveProperty} onDeleteProperty={deleteProperty} />
          </Suspense>
        ) : (
          <>
            <PageBackdrop />
            <EnquiryModal open={enquiryOpen} onClose={() => { setEnquiryOpen(false); setEqStatus(null); }} eqSubject={eqSubject} setEqSubject={setEqSubject} eqForm={eqForm} setEqForm={setEqForm} eqStatus={eqStatus} sendEnquiry={sendEnquiry} />
            <ScrollToTop />
            <Nav />
            <Suspense fallback={<PageLoader />}>
              <RouteReadySignal onReady={markRouteReady} />
              <RouteFade>
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/catalogue" element={<CatalogPage />} />
                  <Route path="/catalogue/:id" element={<PropertyDetail />} />
                  <Route path="/digital-twins" element={<DigitalTwinsPage />} />
                  <Route path="/archive" element={<ArchivePage />} />
                  <Route path="/archive/:id" element={<ArchiveDetail />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/privacy" element={<PrivacyPolicy />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </RouteFade>
            </Suspense>
            <Footer />
          </>
        )}
      </div>

      {/* Boot veil — sits above everything, hiding the page while it assembles,
          then fades away to reveal the finished article underneath. */}
      {!veilGone && (
        <div className={veilFading ? "boot-veil boot-veil-out" : "boot-veil"} aria-hidden={veilFading ? "true" : undefined}>
          {/* 480px on desktop; on narrower screens it falls back to 45vw so it
              stays a comfortable fraction of the viewport instead of running
              off the edges of a phone. 45vw only wins below ~1067px wide, so
              desktop keeps the full 480px. */}
          <div className="boot-veil-seal">
            <SealLoader size="min(480px, 45vw)" color={C.goldDeep} />
          </div>
        </div>
      )}
    </AppContext.Provider>
  );
}

